/* eslint-disable indent */
import { Trans, useTranslation } from "next-i18next"
import styles from "./CourseMain.module.scss"
import { chapterBasicData, CourseContent } from "interfaces/courses"
import { FC } from "react"
import CourseProgressBar from "@cs/components/courses/course-progress-bar/CourseProgressBar"
import { IoIosArrowForward } from "react-icons/io"
import Switch from "@cs/components/courses/courses-switch/CoursesSwitch"
import Link from "next/dist/client/link"
import { useRouter } from "next/router"
import Image from "next/dist/client/image"
import SoftwareEarthly from "@cs/public/img/earthlies/software-earthly.png"
import { useProgress } from "@cs/lib/progress-v6/ProgressContext"
import { Skeleton } from "@cs/components/ui/loading-skeleton"
import { SignInDialog } from "@cs/components/signin-dialog"
import { Button } from "@cs/components/ui/button"

interface CourseMainProps extends CourseContent {
  chaptersData: {
    advanced: (chapterBasicData | undefined)[]
    simple: (chapterBasicData | undefined)[]
  }
  id: string
  difficulty: string
}

const getChapterState = (isStarted: boolean, isCompleted: boolean) => {
  if (!isStarted) return "not-started"
  if (!isCompleted) return "active"
  return "completed"
}

const CourseMain: FC<CourseMainProps> = ({ chaptersData, difficulty, slug, id }) => {
  const router = useRouter()
  const { t } = useTranslation("courses")
  const { progress, isLoading } = useProgress()
  const { advanced, simple } = chaptersData

  const isAdvanced: boolean = difficulty == "advanced"
  const chapters = isAdvanced ? advanced : simple
  const chapter = chapters[0]
  const section = chapter?.sections[0]
  const isSingleLevelOfDifficulty: boolean = simple.length == 0
  const isStarted = !!progress.courses?.[id]?.startedAt
  const isCompleted = !!progress.courses?.[id]?.completedAt
  const completedChapters = chapters.filter(
    chapter => chapter && !!progress.chapters?.[chapter.id]?.completedAt,
  )
  const progressValue: number = Math.floor((completedChapters.length / (chapters.length + 1)) * 100)

  const handleChangeDifficulty = () => {
    router.query.difficulty = isAdvanced ? "simple" : "advanced"
    router.push(
      {
        query: { ...router.query },
      },
      undefined,
      { shallow: true },
    )
  }

  return (
    <main className={styles.container}>
      <section className={styles.content}>
        <h2 className={styles.h2}>{t("what-will-you-learn")}</h2>
        {isLoading ? (
          <div>
            <Skeleton style={{ marginBottom: "8px" }} />
            <Skeleton style={{ marginBottom: "8px" }} />
            <Skeleton style={{ marginBottom: "8px" }} />
          </div>
        ) : (
          <ol className={styles.chapters}>
            {chapters.map((chapter, index) => {
              const isStarted: boolean = !!chapter && !!progress.chapters?.[chapter.id]?.startedAt
              const isCompleted: boolean =
                !!chapter && !!progress.chapters?.[chapter.id]?.completedAt

              return (
                <>
                  {chapter && (
                    <li
                      key={index}
                      className={`
                      ${styles.chapter}
                      ${styles[getChapterState(isStarted, isCompleted)]}
                    `}
                    >
                      <Link
                        href={`${router.query.courseSlug}/${chapter.slug}/${chapter.sections[0].slug}`}
                      >
                        <a> {chapter.title} </a>
                      </Link>
                    </li>
                  )}
                </>
              )
            })}
          </ol>
        )}
      </section>

      {isLoading && <Skeleton style={{ height: "230px" }} />}

      {!isLoading && isStarted && (
        <section className={styles.progressSection}>
          {isCompleted && (
            <>
              <p className={styles.p}>
                <Trans t={t} i18nKey="course-completed" components={[<strong key="0" />]} />
              </p>
              <SignInDialog redirectUrl={`/new-courses/${slug}/certificate`}>
                {isLoading ? (
                  <Skeleton style={{ width: "120px", height: "20px" }} />
                ) : (
                  <Button className={styles.continueButton}>
                    {t("get-cert")}
                    <IoIosArrowForward />
                  </Button>
                )}
              </SignInDialog>
            </>
          )}

          {!isCompleted && (
            <>
              <p className={styles.p}>
                <Trans
                  t={t}
                  i18nKey="course-progress"
                  components={[<strong key="0" />]}
                  values={{ progress: progressValue }}
                />
              </p>
              <CourseProgressBar value={progressValue} max={100} />
              <SignInDialog
                redirectUrl={`/new-courses/${slug}/${chapter?.slug}/${section?.slug}`}
                allowSkip
              >
                {isLoading ? (
                  <Skeleton style={{ width: "120px", height: "20px" }} />
                ) : (
                  <Button className={styles.continueButton}>
                    {isStarted ? t("continue") : t("enroll")}
                    <IoIosArrowForward />
                  </Button>
                )}
              </SignInDialog>
            </>
          )}
        </section>
      )}

      {!isLoading && !isStarted && (
        <section className={styles.newUser}>
          <figure className={styles.softwareEarthly}>
            <Image
              src={SoftwareEarthly}
              layout="responsive"
              alt="Software Earthly"
              width={1}
              height={1}
            />
          </figure>
          {!isSingleLevelOfDifficulty && (
            <section className={styles.difficultySection}>
              <Switch checked={simple.length > 0} onClick={handleChangeDifficulty} />
              <p className={styles.p}>
                <Trans
                  t={t}
                  i18nKey="switch-difficulty"
                  values={{ difficulty: t(isAdvanced ? "simple" : "advanced") }}
                  components={[
                    <span onClick={handleChangeDifficulty} key="0" className={styles.underlined} />,
                  ]}
                />
              </p>
            </section>
          )}
        </section>
      )}
    </main>
  )
}

export default CourseMain
